/**
 * Route Generator Utility (v4.0 — aligned with backend JSONB)
 *
 * Per ROUTE_INFO_DESIGN.md v1.4:
 * - Primary path: parse route_nodes JSON from backend (source of truth)
 * - Fallback: generate route locally from task fields (legacy, with warning)
 * - Supports 2-node (Pick&Drop), 3-node (Liveload), and N-node layouts
 * - Backend provides node status — frontend trusts it
 */

import {
  ContainerTask,
  LocationType,
  RouteNode,
  TaskRoute,
  NodeAction,
  NodeStatus,
  AppointmentType,
  AppointmentStatus
} from '../types/container'

// Re-export types for backwards compatibility


interface RouteConfig {
  originType: LocationType
  originAction: NodeAction
  originAppointment: AppointmentType
  destinationType: LocationType
  destinationAction: NodeAction
  destinationAppointment: AppointmentType
}

// Route configuration for each task type (2-node scenarios)
const TASK_TYPE_ROUTES: Record<string, RouteConfig> = {
  // Import Pick&Drop: Pick from Port, Deliver to Warehouse
  'load-out': {
    originType: 'port',
    originAction: 'pick',
    originAppointment: 'port',
    destinationType: 'warehouse',
    destinationAction: 'delivery',
    destinationAppointment: 'warehouse'
  },
  // Empty Return: Pick from Warehouse, Return to Port
  'empty-in': {
    originType: 'warehouse',
    originAction: 'pick',
    originAppointment: 'none',
    destinationType: 'port',
    destinationAction: 'return',
    destinationAppointment: 'port'
  },
  // Empty Pick: Pick from Port, Deliver to Warehouse
  'empty-out': {
    originType: 'port',
    originAction: 'pick',
    originAppointment: 'port',
    destinationType: 'warehouse',
    destinationAction: 'delivery',
    destinationAppointment: 'warehouse'
  },
  // Export Pick&Drop: Pick from Warehouse, Deliver to Port
  'load-in': {
    originType: 'warehouse',
    originAction: 'pick',
    originAppointment: 'warehouse',
    destinationType: 'port',
    destinationAction: 'return',
    destinationAppointment: 'port'
  },
  // Yard to Warehouse Delivery (Pick&Drop: 2 nodes)
  'delivery': {
    originType: 'yard',
    originAction: 'pick',
    originAppointment: 'none',
    destinationType: 'warehouse',
    destinationAction: 'delivery',
    destinationAppointment: 'warehouse'
  },
  // Warehouse to Yard Return
  'return': {
    originType: 'warehouse',
    originAction: 'pick',
    originAppointment: 'none',
    destinationType: 'yard',
    destinationAction: 'return',
    destinationAppointment: 'none'
  }
}

// =====================================================================
// NODE STATUS CALCULATION (kept for local fallback)
// =====================================================================

const STATUS_ORDER = [
  'on-vessel', 'released', 'on-hold', 'yard-stored', 'pcc-hold',
  'unassigned', 'assigned',
  'picking',
  'delivering', 'on-stop', 'loading', 'empty', 'loaded',
  'returning',
  'completed', 'cancelled', 'paused'
]

const isAfterStatus = (status: string, refStatus: string): boolean => {
  return STATUS_ORDER.indexOf(status) > STATUS_ORDER.indexOf(refStatus)
}

const getNodeStatusForPickDrop = (taskStatus: string, nodeAction: NodeAction): NodeStatus => {
  if (nodeAction === 'pick') {
    if (taskStatus === 'picking') return 'in-progress'
    if (isAfterStatus(taskStatus, 'picking')) return 'completed'
    return 'pending'
  }
  if (nodeAction === 'delivery' || nodeAction === 'inspection') {
    if (['delivering', 'on-stop'].includes(taskStatus)) return 'in-progress'
    if (taskStatus === 'completed') return 'completed'
    return 'pending'
  }
  if (nodeAction === 'return') {
    if (['delivering', 'returning'].includes(taskStatus)) return 'in-progress'
    if (taskStatus === 'completed') return 'completed'
    return 'pending'
  }
  if (nodeAction === 'store') {
    if (['delivering', 'on-stop'].includes(taskStatus)) return 'in-progress'
    if (['completed', 'yard-stored'].includes(taskStatus)) return 'completed'
    return 'pending'
  }
  return 'pending'
}

const getNodeStatusForLiveload = (taskStatus: string, nodeAction: NodeAction): NodeStatus => {
  if (nodeAction === 'pick') {
    if (taskStatus === 'picking') return 'in-progress'
    if (isAfterStatus(taskStatus, 'picking')) return 'completed'
    return 'pending'
  }
  if (nodeAction === 'delivery') {
    if (['delivering', 'on-stop', 'loading', 'empty', 'loaded'].includes(taskStatus)) return 'in-progress'
    if (['returning', 'completed'].includes(taskStatus)) return 'completed'
    return 'pending'
  }
  if (nodeAction === 'return') {
    if (taskStatus === 'returning') return 'in-progress'
    if (taskStatus === 'completed') return 'completed'
    return 'pending'
  }
  return 'pending'
}

// =====================================================================
// APPOINTMENT HELPERS
// =====================================================================

const deriveAppointmentStatus = (
  appointmentTime?: string,
  taskStatus?: string,
  explicitStatus?: AppointmentStatus
): AppointmentStatus => {
  if (explicitStatus && explicitStatus !== 'pending') return explicitStatus
  if (!appointmentTime || appointmentTime === 'Pending') return 'pending'
  if (taskStatus === 'completed') return 'completed'
  const apptDate = new Date(appointmentTime)
  if (apptDate < new Date()) return 'confirmed'
  return 'scheduled'
}

// =====================================================================
// ROUTE GENERATION
// =====================================================================

/**
 * Determine if task is a Liveload with 3 nodes
 */
export const isLiveload3NodeTask = (task: ContainerTask): boolean => {
  if (task.taskType === 'delivery' && task.taskSubtype === 'live-load') {
    return true
  }
  if (task.pendingYardStorage || task.pendingPccInspection || task.destinationType === 'yard' || task.destinationType === 'pcc') {
    return false
  }
  if (task.taskSubtype === 'live-load' && ['load-out', 'empty-out'].includes(task.taskType)) {
    return true
  }
  return false
}

/**
 * Check if task has rejected node
 */
export const hasRejectedNode = (task: ContainerTask): boolean => {
  return task.hasRejectedNode === true
}

/**
 * Get number of route nodes for a task
 */
export const getRouteNodeCount = (task: ContainerTask): number => {
  if (task.routeNodeCount) return task.routeNodeCount
  const baseCount = isLiveload3NodeTask(task) ? 3 : 2
  return hasRejectedNode(task) ? baseCount + 1 : baseCount
}

/**
 * Generate route nodes for a task — MAIN ENTRY POINT
 *
 * Primary: use route_nodes JSON from backend (source of truth)
 * Fallback: generate locally from task fields (legacy)
 */
export const generateRouteNodes = (task: ContainerTask): TaskRoute => {
  const routeNodesJson = task.routeNodes || (task as Record<string, unknown>).route_nodes
  if (routeNodesJson && typeof routeNodesJson === 'string') {
    try {
      const parsed = JSON.parse(routeNodesJson)
      if (Array.isArray(parsed) && parsed.length > 0) {
        return generateRouteFromJson(task, parsed)
      }
    } catch (e) {
      console.warn('[RouteGenerator] Failed to parse route_nodes JSON:', e)
    }
  } else if (Array.isArray(routeNodesJson) && routeNodesJson.length > 0) {
    return generateRouteFromJson(task, routeNodesJson)
  }

  // Fallback: generate locally (legacy path)
  console.warn('[RouteGenerator] No route_nodes JSON — using local fallback for task', task.taskId)

  if (hasRejectedNode(task)) {
    return generateRejectedRouteFallback(task)
  }
  if (isLiveload3NodeTask(task)) {
    return generateLiveloadRouteFallback(task)
  }
  return generatePickDropRouteFallback(task)
}

/**
 * Generate route nodes from route_nodes JSON data (primary path)
 * Maps backend fields directly — names already match after v1.4 alignment
 */
interface RouteNodeJson {
  type: string
  id?: number
  name: string
  address?: string
  action?: string
  status?: string
  appointmentTime?: string
  appointmentEnd?: string
  /** Migration 174 — 「当天，不限时间点」 */
  appointmentAllDay?: boolean
  appointmentStatus?: string
  gateCode?: string
  // Resolved server-side by RouteNodeEnrichService — intermediate stops have no
  // flat task column to fall back on, so these are the only source for them.
  shortName?: string
  fullName?: string
  city?: string
  appointmentRequired?: boolean
}

const generateRouteFromJson = (task: ContainerTask, routeNodesJson: RouteNodeJson[]): TaskRoute => {
  const nodes: TaskRoute = []
  const isLiveload = task.taskSubtype === 'live-load'
  const nodeCount = routeNodesJson.length

  routeNodesJson.forEach((jsonNode, index) => {
    // Determine action: trust backend if present, else infer from position
    let action: NodeAction
    if (jsonNode.action) {
      action = jsonNode.action as NodeAction
    } else if (index === 0) {
      action = 'pick'
    } else if (index === nodeCount - 1) {
      action = isLiveload && nodeCount >= 3 ? 'return' : 'delivery'
    } else if (jsonNode.type === 'pcc') {
      action = 'inspection'
    } else if (jsonNode.type === 'yard' && index > 0 && index < nodeCount - 1) {
      action = 'store'
    } else {
      action = 'delivery'
    }

    // Determine appointment type
    let appointmentType: AppointmentType = 'none'
    if (jsonNode.type === 'port') appointmentType = 'port'
    else if (jsonNode.type === 'warehouse') appointmentType = 'warehouse'

    // Get appointment details
    let appointmentStatus: AppointmentStatus = 'pending'
    let appointmentTime: string | undefined
    let appointmentEnd: string | undefined
    let appointmentNumber: string | undefined
    let gateCode: string | undefined
    // 「当天，不限时间点」——和 appointmentTime 走同一条来源，否则显示成 00:00
    let appointmentAllDay: boolean | undefined

    if (jsonNode.type === 'port') {
      // Source-of-truth is the appointments table, surfaced in the task detail
      // modal via useAppointments(). This helper runs over the dispatch grid's
      // snapshot of the task, where appointments aren't fetched separately —
      // so we read the `route_nodes` JSONB mirror (kept in sync by
      // AppointmentService.setPortAppointment post-WP4-b).
      const nodeApptTime = jsonNode.appointmentTime
      const nodeApptEnd = jsonNode.appointmentEnd
      const nodeApptStatus = jsonNode.appointmentStatus
      const nodeGateCode = jsonNode.gateCode

      if (nodeApptTime) {
        // Use per-node data (works for both pick and return ports)
        appointmentTime = nodeApptTime
        appointmentEnd = nodeApptEnd
        appointmentAllDay = jsonNode.appointmentAllDay
        gateCode = nodeGateCode || task.gateCode
        // 提柜那一站多看一眼扁平列。镜像是会被别处盖回去的（任何一次带上预约
        // 字段的任务更新都会把它写成 scheduled），而「码头把票作废了」这件事
        // 扁平列一定是对的 —— 两边打架时按作废算，宁可让人多点一下，也不能让
        // 板子上挂着一张不存在的约。
        const cancelledFlat = action === 'pick' && task.portAppointmentStatus === 'cancelled'
        appointmentStatus = cancelledFlat
          ? 'cancelled'
          : deriveAppointmentStatus(nodeApptTime, task.status, nodeApptStatus)
      } else if (action === 'pick') {
        // Defensive fallback to the tasks flat columns. Shouldn't normally
        // trigger post-WP4-b — AppointmentService always writes the node slot.
        // Kept for legacy rows that pre-date the sync contract.
        appointmentTime = task.portAppointment
        appointmentEnd = task.portAppointmentEnd
        appointmentAllDay = task.portAppointmentAllDay
        gateCode = task.gateCode
        appointmentStatus = deriveAppointmentStatus(task.portAppointment, task.status, task.portAppointmentStatus)
      }
      // else: return port with no per-node appointment → stays 'pending'
    } else if (jsonNode.type === 'warehouse') {
      // Warehouse is a single-destination concept (no per-leg route_node
      // mirror), so we read the tasks flat columns directly. Same caveat —
      // AppointmentService.setWarehouseAppointment keeps these in sync.
      appointmentStatus = deriveAppointmentStatus(task.warehouseAppointment, task.status, task.warehouseAppointmentStatus)
      appointmentTime = task.warehouseAppointment
      appointmentAllDay = task.warehouseAppointmentAllDay
      appointmentNumber = task.appointmentNumber
    }

    // Node status: trust backend if present, else calculate locally
    let status: NodeStatus = 'pending'
    if (jsonNode.status) {
      status = jsonNode.status as NodeStatus
    } else if (isLiveload) {
      status = getNodeStatusForLiveload(task.status, action)
    } else {
      status = getNodeStatusForPickDrop(task.status, action)
    }

    // Prefer short name for display, fall back to JSON name or task fields
    const shortName = jsonNode.shortName || getShortNameForType(task, jsonNode.type as LocationType, jsonNode.id)
    const displayName = shortName || jsonNode.name || getLocationNameFallback(task, jsonNode.type as LocationType)
    const fullName = jsonNode.fullName || jsonNode.name || getLocationNameFallback(task, jsonNode.type as LocationType)

    const node: RouteNode = {
      action,
      type: jsonNode.type as LocationType,
      id: jsonNode.id,
      name: displayName,
      fullName: fullName !== displayName ? fullName : undefined,
      address: jsonNode.address,
      city: jsonNode.city,
      appointmentRequired: jsonNode.appointmentRequired ??
        // Empty-in tasks: picking up empty container from warehouse defaults to no appt required
        (jsonNode.type === 'warehouse' && action === 'pick' && task.taskType === 'empty-in' ? false : undefined),
      appointmentType,
      appointmentStatus: appointmentType === 'none' ? 'none' : appointmentStatus,
      appointmentTime,
      appointmentEnd,
      appointmentAllDay,
      appointmentNumber,
      gateCode,
      status,
      loadingStatus: (jsonNode.type === 'warehouse' && isLiveload && action === 'delivery')
        ? (task.importExport === 'import' ? 'unloading' : 'loading')
        : undefined
    }

    nodes.push(node)
  })

  return nodes
}

/**
 * Get fallback location name when JSON doesn't have it
 */
const getLocationNameFallback = (task: ContainerTask, locationType: string): string => {
  switch (locationType) {
    case 'port':
      return task.portShortName || task.portName || 'Port'
    case 'warehouse':
      return task.warehouseShortName || task.warehouseName || task.warehouse || 'Warehouse'
    case 'yard':
      return task.yardShortName || task.yardName || 'Yard'
    case 'pcc':
      return task.pccLocationName || 'CBP'
    case 'custom':
      return task.destinationAddress || 'Custom Location'
    default:
      return 'Location'
  }
}

/**
 * Get short name from task fields for a given location type.
 * Only returns the short name if the node's ID matches the task's location ID
 * (to avoid returning wrong short name for return-port vs pick-port scenarios).
 */
const getShortNameForType = (task: ContainerTask, locationType: string, nodeId?: number | null): string | undefined => {
  switch (locationType) {
    case 'port':
      // Only use portShortName if this node matches the task's port
      if (nodeId && task.portId && nodeId !== task.portId) return undefined
      return task.portShortName || undefined
    case 'warehouse':
      if (nodeId && task.warehouseId && nodeId !== task.warehouseId) return undefined
      return task.warehouseShortName || undefined
    case 'yard':
      if (nodeId && task.yardId && nodeId !== task.yardId) return undefined
      return task.yardShortName || undefined
    default:
      return undefined
  }
}

// =====================================================================
// LOCAL FALLBACK GENERATORS (used when no route_nodes JSON available)
// =====================================================================

/**
 * Get location name for fallback generation
 */
function getLocationName(task: ContainerTask, locationType: LocationType, nodePosition: 'origin' | 'destination'): string {
  if (nodePosition === 'origin' && task.originName) return task.originName
  if (nodePosition === 'destination' && task.destinationName) return task.destinationName

  let typeSpecificName: string | undefined
  switch (locationType) {
    case 'port':
      typeSpecificName = task.portShortName || task.portName
      if (nodePosition === 'destination' && !task.destinationId) typeSpecificName = undefined
      break
    case 'warehouse':
      typeSpecificName = task.warehouseShortName || task.warehouseName || task.warehouse
      break
    case 'yard':
      typeSpecificName = task.yardShortName || task.yardName
      break
    case 'pcc':
      typeSpecificName = task.pccLocationName
      break
    case 'shipper':
      typeSpecificName = task.shipperName || task.shipper
      break
    case 'custom':
      typeSpecificName = task.destinationAddress || task.destinationName
      break
  }

  return typeSpecificName?.trim() || 'TBD'
}

function getLocationCity(task: ContainerTask, locationType: LocationType, nodePosition: 'origin' | 'destination'): string | undefined {
  if (nodePosition === 'origin' && task.originCity) return task.originCity
  if (nodePosition === 'destination' && task.destinationCity) return task.destinationCity
  if (locationType === 'port' && nodePosition === 'destination' && !task.destinationId) return undefined
  switch (locationType) {
    case 'port': return [task.portCity, task.portState].filter(Boolean).join(', ') || undefined
    case 'warehouse': return [task.warehouseCity, task.warehouseState].filter(Boolean).join(', ') || undefined
    case 'yard': return [task.yardCity, task.yardState].filter(Boolean).join(', ') || undefined
    case 'pcc': return task.pccLocationCity || undefined
    default: return undefined
  }
}

function getLocationAddress(task: ContainerTask, locationType: LocationType, nodePosition: 'origin' | 'destination'): string | undefined {
  if (nodePosition === 'origin' && task.originAddress) return task.originAddress
  if (nodePosition === 'destination' && task.destinationAddress) return task.destinationAddress
  if (locationType === 'port' && nodePosition === 'destination' && !task.destinationId) return undefined
  switch (locationType) {
    case 'port': return task.portAddress
    case 'warehouse': return task.warehouseAddress
    case 'yard': return task.yardAddress
    case 'pcc': return task.pccLocationAddress
    default: return undefined
  }
}

function getOriginAppointmentTime(task: ContainerTask, config: RouteConfig): string | undefined {
  if (config.originAppointment === 'port') return task.portAppointment
  if (config.originAppointment === 'warehouse') return task.warehouseAppointment
  return undefined
}

function getDestinationAppointmentTime(task: ContainerTask, config: RouteConfig): string | undefined {
  if (config.destinationAppointment === 'port') return task.portAppointment
  if (config.destinationAppointment === 'warehouse') return task.warehouseAppointment
  return undefined
}

function getOriginAppointmentStatus(task: ContainerTask, config: RouteConfig): AppointmentStatus {
  const time = getOriginAppointmentTime(task, config)
  const explicitStatus = config.originAppointment === 'port' ? task.portAppointmentStatus : task.warehouseAppointmentStatus
  return deriveAppointmentStatus(time, task.status, explicitStatus)
}

function getDestinationAppointmentStatus(task: ContainerTask, config: RouteConfig): AppointmentStatus {
  const time = getDestinationAppointmentTime(task, config)
  const explicitStatus = config.destinationAppointment === 'port' ? task.portAppointmentStatus : task.warehouseAppointmentStatus
  return deriveAppointmentStatus(time, task.status, explicitStatus)
}

/**
 * Fallback: Generate 2-node route for Pick&Drop
 */
const generatePickDropRouteFallback = (task: ContainerTask): TaskRoute => {
  const config = TASK_TYPE_ROUTES[task.taskType]
  if (!config) {
    console.warn(`Unknown task type: ${task.taskType}, using load-out as default`)
    return generatePickDropRouteFallback({ ...task, taskType: 'load-out' })
  }

  const originName = getLocationName(task, config.originType, 'origin')
  const isOriginTBD = originName === 'TBD'
  const actualOriginApptType: AppointmentType = isOriginTBD ? 'none' : config.originAppointment

  const actualDestType = task.destinationType || config.destinationType
  const destName = getLocationName(task, actualDestType, 'destination')
  const isDestTBD = destName === 'TBD'
  const actualDestApptType: AppointmentType = isDestTBD ? 'none'
    : actualDestType === 'yard' || actualDestType === 'pcc' ? 'none'
    : actualDestType === 'port' ? 'port'
    : actualDestType === 'warehouse' ? 'warehouse'
    : config.destinationAppointment
  // Only override to 'store'/'inspection' when destination differs from config's natural destination
  const actualDestAction: NodeAction = (actualDestType === 'yard' && config.destinationType !== 'yard') ? 'store'
    : (actualDestType === 'pcc' && config.destinationType !== 'pcc') ? 'inspection'
    : config.destinationAction

  return [
    {
      action: 'pick',
      type: task.originType || config.originType,
      id: task.originId,
      name: originName,
      city: isOriginTBD ? undefined : getLocationCity(task, config.originType, 'origin'),
      address: isOriginTBD ? undefined : getLocationAddress(task, config.originType, 'origin'),
      appointmentType: actualOriginApptType,
      appointmentRequired: task.taskType === 'empty-in' && config.originType === 'warehouse' ? false : undefined,
      appointmentStatus: isOriginTBD ? 'none' : getOriginAppointmentStatus(task, config),
      appointmentTime: isOriginTBD ? undefined : getOriginAppointmentTime(task, config),
      appointmentEnd: actualOriginApptType === 'port' && !isOriginTBD ? task.portAppointmentEnd : undefined,
      appointmentNumber: actualOriginApptType === 'warehouse' && !isOriginTBD ? task.appointmentNumber : undefined,
      gateCode: actualOriginApptType === 'port' && !isOriginTBD ? task.gateCode : undefined,
      status: getNodeStatusForPickDrop(task.status, 'pick'),
      pendingDecision: isOriginTBD
    },
    {
      action: actualDestAction,
      type: actualDestType,
      id: task.destinationId,
      name: destName,
      city: isDestTBD ? undefined : getLocationCity(task, actualDestType, 'destination'),
      address: isDestTBD ? undefined : getLocationAddress(task, actualDestType, 'destination'),
      appointmentType: actualDestApptType,
      appointmentStatus: actualDestApptType === 'none' ? 'none' : getDestinationAppointmentStatus(task, config),
      appointmentTime: isDestTBD ? undefined : getDestinationAppointmentTime(task, config),
      appointmentEnd: actualDestApptType === 'port' && !isDestTBD ? task.portAppointmentEnd : undefined,
      appointmentNumber: actualDestApptType === 'warehouse' && !isDestTBD ? task.appointmentNumber : undefined,
      gateCode: actualDestApptType === 'port' && !isDestTBD ? task.gateCode : undefined,
      status: getNodeStatusForPickDrop(task.status, actualDestAction),
      pendingDecision: isDestTBD
    }
  ]
}

/**
 * Fallback: Generate 3-node route for Liveload
 */
const generateLiveloadRouteFallback = (task: ContainerTask): TaskRoute => {
  const isImport = task.importExport === 'import'
  const isDeliveryTask = task.taskType === 'delivery'
  const pickLocationType: LocationType = isDeliveryTask ? 'yard' : 'port'

  const warehouseName = getLocationName(task, 'warehouse', 'destination')
  const isWarehouseTBD = warehouseName === 'TBD'

  // Determine return location
  let returnLocationType: LocationType = 'port'
  let pendingDecision = false
  let returnLocationPending = false

  if (isDeliveryTask) {
    if (task.pendingDeliveryDestinationType) {
      returnLocationType = task.pendingDeliveryDestinationType
    } else if (['empty', 'loaded'].includes(task.status)) {
      pendingDecision = true
      returnLocationType = 'yard'
    } else if (task.taskSubtype === 'live-load') {
      const hasReturnLocation = task.returnLocationId || task.pendingDeliveryDestinationId
      if (hasReturnLocation) {
        returnLocationType = task.returnLocationType || 'port'
      } else {
        returnLocationPending = true
      }
    }
  } else {
    const hasReturnLocationInfo = task.returnLocationId || task.pendingDeliveryDestinationId
    if (!hasReturnLocationInfo) returnLocationPending = true
  }

  // Resolve return location details
  let returnLocationId: number | undefined
  let returnLocationName: string
  let returnLocationFullName: string | undefined
  let returnLocationCity: string | undefined
  let returnLocationAddress: string | undefined

  if (returnLocationType === 'yard') {
    returnLocationId = task.yardId || task.pendingDeliveryDestinationId
    returnLocationName = task.yardShortName || task.yardName || 'Yard'
    returnLocationFullName = task.yardName
    returnLocationCity = [task.yardCity, task.yardState].filter(Boolean).join(', ') || undefined
    returnLocationAddress = task.yardAddress
  } else {
    const returnLocId = task.returnLocationId || task.pendingDeliveryDestinationId
    if (returnLocationPending || !returnLocId) {
      returnLocationId = undefined
      returnLocationName = 'TBD'
    } else {
      returnLocationId = returnLocId
      const resolvedShortName = task.returnLocationShortName
      const resolvedFullName = task.returnLocationName
      const resolvedCity = task.returnLocationCity
      const resolvedAddress = task.returnLocationAddress
      returnLocationName = resolvedShortName || resolvedFullName || task.portShortName || task.portName || 'Port'
      returnLocationFullName = resolvedFullName || resolvedShortName || task.portName
      returnLocationCity = resolvedCity || [task.portCity, task.portState].filter(Boolean).join(', ') || undefined
      returnLocationAddress = resolvedAddress || task.portAddress
    }
  }

  return [
    {
      action: 'pick',
      type: pickLocationType,
      id: task.originId,
      name: getLocationName(task, pickLocationType, 'origin'),
      city: getLocationCity(task, pickLocationType, 'origin'),
      address: getLocationAddress(task, pickLocationType, 'origin'),
      appointmentType: pickLocationType === 'port' ? 'port' : 'none',
      appointmentStatus: pickLocationType === 'port'
        ? deriveAppointmentStatus(task.portAppointment, task.status, task.portAppointmentStatus)
        : 'none',
      appointmentTime: pickLocationType === 'port' ? task.portAppointment : undefined,
      appointmentEnd: pickLocationType === 'port' ? task.portAppointmentEnd : undefined,
      appointmentAllDay: pickLocationType === 'port' ? task.portAppointmentAllDay : undefined,
      gateCode: pickLocationType === 'port' ? task.gateCode : undefined,
      status: getNodeStatusForLiveload(task.status, 'pick')
    },
    {
      action: 'delivery',
      type: 'warehouse',
      id: task.warehouseId || task.destinationId,
      name: warehouseName,
      city: isWarehouseTBD ? undefined : ([task.warehouseCity, task.warehouseState].filter(Boolean).join(', ') || undefined),
      address: isWarehouseTBD ? undefined : task.warehouseAddress,
      appointmentType: isWarehouseTBD ? 'none' : 'warehouse',
      appointmentStatus: isWarehouseTBD ? 'none' : deriveAppointmentStatus(task.warehouseAppointment, task.status, task.warehouseAppointmentStatus),
      appointmentTime: isWarehouseTBD ? undefined : task.warehouseAppointment,
      appointmentAllDay: isWarehouseTBD ? undefined : task.warehouseAppointmentAllDay,
      appointmentNumber: isWarehouseTBD ? undefined : task.appointmentNumber,
      status: getNodeStatusForLiveload(task.status, 'delivery'),
      loadingStatus: isImport ? 'unloading' : 'loading',
      pendingDecision: isWarehouseTBD
    },
    {
      action: 'return',
      type: returnLocationType,
      id: returnLocationId,
      name: returnLocationName,
      fullName: returnLocationFullName,
      city: returnLocationCity,
      address: returnLocationAddress,
      appointmentType: returnLocationType === 'port' && !returnLocationPending ? 'port' : 'none',
      appointmentStatus: returnLocationType === 'port' && !returnLocationPending
        ? deriveAppointmentStatus(undefined, task.status)
        : 'none',
      status: getNodeStatusForLiveload(task.status, 'return'),
      pendingDecision: pendingDecision || returnLocationPending
    }
  ]
}

/**
 * Fallback: Generate route for Rejected scenarios
 */
const generateRejectedRouteFallback = (task: ContainerTask): TaskRoute => {
  const nodes: TaskRoute = []
  const isLiveload = task.taskSubtype === 'live-load'
  const pickLocationType: LocationType = task.originType || 'port'

  // Node 1: Pick (always completed in rejected scenario)
  nodes.push({
    action: 'pick',
    type: pickLocationType,
    id: task.originId,
    name: getLocationName(task, pickLocationType, 'origin'),
    city: getLocationCity(task, pickLocationType, 'origin'),
    address: getLocationAddress(task, pickLocationType, 'origin'),
    appointmentType: pickLocationType === 'port' ? 'port' : 'none',
    appointmentStatus: pickLocationType === 'port'
      ? deriveAppointmentStatus(task.portAppointment, task.status, task.portAppointmentStatus)
      : 'none',
    appointmentTime: pickLocationType === 'port' ? task.portAppointment : undefined,
    appointmentEnd: pickLocationType === 'port' ? task.portAppointmentEnd : undefined,
    appointmentAllDay: pickLocationType === 'port' ? task.portAppointmentAllDay : undefined,
    gateCode: pickLocationType === 'port' ? task.gateCode : undefined,
    status: 'completed'
  })

  // Node 2: Rejected location
  const rejectedLocationType: LocationType = task.rejectedLocationType || task.originalDestinationType || 'warehouse'
  nodes.push({
    action: 'rejected',
    type: rejectedLocationType,
    id: task.rejectedLocationId || task.originalDestinationId,
    name: task.rejectedLocationName || task.originalDestinationName || 'Rejected Location',
    appointmentType: 'none',
    appointmentStatus: 'none',
    status: 'rejected',
    rejectedAt: task.rejectedAt
  })

  // Node 3: New destination
  const destLocationType: LocationType = task.destinationType || 'warehouse'
  const destAction: NodeAction = destLocationType === 'yard' ? 'store' : 'delivery'
  const destNodeStatus: NodeStatus = (() => {
    if (destAction === 'store') {
      if (['delivering', 'on-stop'].includes(task.status)) return 'in-progress'
      if (task.status === 'completed') return 'completed'
      return 'pending'
    }
    if (['delivering', 'on-stop', 'loading'].includes(task.status)) return 'in-progress'
    if (['empty', 'loaded', 'returning', 'completed'].includes(task.status)) return 'completed'
    return 'pending'
  })()

  nodes.push({
    action: destAction,
    type: destLocationType,
    id: task.destinationId,
    name: getLocationName(task, destLocationType, 'destination'),
    city: getLocationCity(task, destLocationType, 'destination'),
    address: getLocationAddress(task, destLocationType, 'destination'),
    appointmentType: destLocationType === 'warehouse' ? 'warehouse' : 'none',
    appointmentStatus: destLocationType === 'warehouse'
      ? deriveAppointmentStatus(task.warehouseAppointment, task.status, task.warehouseAppointmentStatus)
      : 'none',
    appointmentTime: destLocationType === 'warehouse' ? task.warehouseAppointment : undefined,
    appointmentNumber: destLocationType === 'warehouse' ? task.appointmentNumber : undefined,
    status: destNodeStatus
  })

  // Node 4: Return (only for Liveload and if not going to yard)
  if (isLiveload && destLocationType !== 'yard') {
    const returnLocationType: LocationType = task.pendingDeliveryDestinationType || 'port'
    const returnStatus: NodeStatus = task.status === 'returning' ? 'in-progress'
      : task.status === 'completed' ? 'completed' : 'pending'
    nodes.push({
      action: 'return',
      type: returnLocationType,
      id: returnLocationType === 'yard' ? (task.yardId ?? undefined) : (task.portId ?? undefined),
      name: returnLocationType === 'yard'
        ? (task.yardShortName || task.yardName || 'Yard')
        : (task.portShortName || task.portName || 'Port'),
      city: returnLocationType === 'yard'
        ? [task.yardCity, task.yardState].filter(Boolean).join(', ') || undefined
        : [task.portCity, task.portState].filter(Boolean).join(', ') || undefined,
      address: returnLocationType === 'yard' ? task.yardAddress : task.portAddress,
      appointmentType: returnLocationType === 'port' ? 'port' : 'none',
      appointmentStatus: 'none',
      status: returnStatus
    })
  }

  return nodes
}

// =====================================================================
// UTILITY EXPORTS
// =====================================================================

export const requiresPortAppointment = (taskType: string): boolean => {
  const config = TASK_TYPE_ROUTES[taskType]
  if (!config) return false
  return config.originAppointment === 'port' || config.destinationAppointment === 'port'
}

export const requiresWarehouseAppointment = (taskType: string): boolean => {
  const config = TASK_TYPE_ROUTES[taskType]
  if (!config) return false
  return config.originAppointment === 'warehouse' || config.destinationAppointment === 'warehouse'
}

export const getRouteSummary = (task: ContainerTask): string => {
  if (isLiveload3NodeTask(task)) {
    if (task.taskType === 'delivery') return 'Yard -> Warehouse (Liveload) -> Return'
    return task.importExport === 'import'
      ? 'Port -> Warehouse (Liveload) -> Port'
      : 'Port -> Warehouse (Liveload) -> Port'
  }
  const summaries: Record<string, string> = {
    'load-out': 'Port Pickup -> Warehouse Delivery',
    'empty-in': 'Warehouse Pickup -> Port Return',
    'empty-out': 'Port Pickup -> Warehouse Delivery',
    'load-in': 'Warehouse Pickup -> Port Delivery',
    'delivery': 'Yard Pickup -> Warehouse Delivery',
    'return': 'Warehouse Pickup -> Yard Return'
  }
  return summaries[task.taskType] || 'Unknown Route'
}

export const getActionLabel = (action: NodeAction, _locationType?: LocationType): string => {
  switch (action) {
    case 'pick': return 'PICK'
    case 'delivery': return 'DELIVERY'
    case 'return': return 'RETURN'
    case 'rejected': return 'REJECTED'
    case 'store': return 'STORE'
    case 'inspection': return 'INSPECTION'
    case 'handover': return 'HANDOVER'
    default: {
      const _exhaustiveCheck: never = action
      return String(_exhaustiveCheck).toUpperCase()
    }
  }
}

export const getLocationTypeIcon = (locationType: LocationType): string => {
  switch (locationType) {
    case 'port': return 'ship'
    case 'warehouse': return 'warehouse'
    case 'yard': return 'mapPin'
    case 'shipper': return 'building'
    case 'pcc': return 'shieldAlert'
    case 'custom': return 'mapPin'
    default: return 'mapPin'
  }
}

export default {
  generateRouteNodes,
  getRouteNodeCount,
  isLiveload3NodeTask,
  hasRejectedNode,
  requiresPortAppointment,
  requiresWarehouseAppointment,
  getRouteSummary,
  getActionLabel,
  getLocationTypeIcon,
  TASK_TYPE_ROUTES
}
